import axios, { AxiosResponse } from 'axios';
import { configHelper as ch } from '@ilteducation/config';
import {
  GetOrganizationUsersResponse,
  GetOrganizationUsersRequest,
  GetGroupsRequest,
  GetUserResponse,
  GetUserRequest,
  UpdateUserRequest,
  GetMembershipsRequest,
  GetMembershipsResponse,
  GetAssociationsRequest,
  GetAssociationsResponse,
  CreateUserRequest,
  GetGroupsRequestFilters,
  GetGroupsResponse,
  GetGroupRequest,
  GetGroupResponse,
  UpdateGroupRequest,
  UpdateAssociationRequest,
  CreateUserResponse,
  SearchUsersRequest,
  SearchUsersResponse,
  CreateGroupRequest,
  CreateGroupResponse,
  UpdateGroupResponse,
} from '@ilteducation/customer-mgmt-types';
import useQuery from './composables/useQuery';
import type { FetchListOptions } from './types/types';

export const customerMgmt = ch.getApiUrl('customer-mgmt');
const { makeQuery, makeQueryByObject } = useQuery();

export default {
  fetchers: {
    fetchSchoolUnits(options: FetchListOptions) {
      return axios.get(`${customerMgmt}/schoolUnits?${makeQuery(options)}`);
    },

    fetchUserSchoolUnits(options: FetchListOptions) {
      return axios.get(`${customerMgmt}/userSchoolUnits?${makeQuery(options)}`);
    },

    fetchGroups(options: FetchListOptions) {
      return axios.get(`${customerMgmt}/groups?${makeQuery(options)}`);
    },

    fetchUsers(options: FetchListOptions) {
      return axios.get(`${customerMgmt}/users?${makeQuery(options)}`);
    },
  },
  getters: {
    getUsers(userUid: string) {
      return axios.get(`${customerMgmt}/users/${userUid}`);
    },
    getSchoolUnits(schoolUnitUid: string) {
      return axios.get(`${customerMgmt}/schoolUnits/${schoolUnitUid}`);
    },
  },

  associations: {
    get(options: GetAssociationsRequest): Promise<AxiosResponse<GetAssociationsResponse>> {
      return axios.get(`${customerMgmt}/userSchoolUnits?${makeQueryByObject(options)}`);
    },
  },
  memberships: {
    get(options: GetMembershipsRequest): Promise<AxiosResponse<GetMembershipsResponse>> {
      return axios.get(`${customerMgmt}/userGroups?${makeQueryByObject(options)}`);
    },
  },
  user: {
    get(uid: GetUserRequest['userId']): Promise<AxiosResponse<GetUserResponse>> {
      return axios.get(`${customerMgmt}/users/${uid}`);
    },
    create(data: CreateUserRequest): Promise<AxiosResponse<CreateUserResponse>> {
      return axios.post(`${customerMgmt}/users`, data);
    },
    patch(uid: UpdateUserRequest['userId'], data: Omit<UpdateUserRequest, 'userId'>): Promise<AxiosResponse<void>> {
      return axios.patch(`${customerMgmt}/users/${uid}`, data);
    },
    updateAssociation(
      userUid: string,
      schooUnitId: string,
      role: UpdateAssociationRequest['role'],
    ): Promise<AxiosResponse<void>> {
      return axios.put(`${customerMgmt}/users/${userUid}/schoolUnits/${schooUnitId}`, { role });
    },
    deleteAssociation(userUid: string, schoolUnitId: string): Promise<AxiosResponse<void>> {
      return axios.delete(`${customerMgmt}/users/${userUid}/schoolUnits/${schoolUnitId}`);
    },
    deleteMembership(userUid: string, groupUid: string) {
      return axios.delete(`${customerMgmt}/users/${userUid}/groups/${groupUid}`);
    },
    search: (options: SearchUsersRequest): Promise<AxiosResponse<SearchUsersResponse>> => {
      return axios.get(`${customerMgmt}/users/search?${makeQueryByObject(options)}`);
    },
  },
  organization: {
    /**
     * This user list faster then regular users endpoint since permissions are check for only one specific organization.
     */
    users: (
      organizationUid: GetOrganizationUsersRequest['organizationUid'],
      options: Omit<GetOrganizationUsersRequest, 'organizationUid'>,
    ): Promise<AxiosResponse<GetOrganizationUsersResponse>> => {
      return axios.get(`${customerMgmt}/organizations/${organizationUid}/users?${makeQueryByObject(options)}`, {});
    },

    /**
     * @TODO: Implement this endpoint
     * @param organizationUid
     * @param options
     * @returns
     */
    groups: (
      organizationUid: Required<GetGroupsRequestFilters>['schoolUnits'][number],
      options: Omit<GetGroupsRequest, 'schoolUnitId'>,
    ): Promise<AxiosResponse<GetGroupsResponse>> => {
      return axios.get(
        `${customerMgmt}/groups?${makeQueryByObject({
          ...options,
          filters: {
            ...(options.filters ?? {}),
            schoolUnits: [organizationUid],
          },
        } satisfies GetGroupsRequest)}`,
      );
    },
  },
  group: {
    get: (groupId: Required<GetGroupRequest>['groupId']): Promise<AxiosResponse<GetGroupResponse>> => {
      return axios.get(`${customerMgmt}/groups/${groupId}`);
    },
    create: (data: CreateGroupRequest): Promise<AxiosResponse<CreateGroupResponse>> => {
      return axios.put(`${customerMgmt}/groups`, data);
    },
    patch(groupUid: string, data: Omit<UpdateGroupRequest, 'groupUid'>): Promise<AxiosResponse<UpdateGroupResponse>> {
      return axios.patch(`${customerMgmt}/groups/${groupUid}`, data);
    },
    delete(groupUid: string): Promise<AxiosResponse<void>> {
      return axios.delete(`${customerMgmt}/groups/${groupUid}`);
    },
    addMember: (groupUid: string, userUid: string): Promise<AxiosResponse<void>> => {
      return axios.put(`${customerMgmt}/users/${userUid}/groups/${groupUid}`);
    },
    removeMember: (groupUid: string, userUid: string): Promise<AxiosResponse<void>> => {
      return axios.delete(`${customerMgmt}/users/${userUid}/groups/${groupUid}`);
    },
    users: (
      groupId: string,
      options: Omit<SearchUsersRequest, 'filters'>,
    ): Promise<AxiosResponse<SearchUsersResponse>> => {
      const requestOptions = {
        ...options,
        filters: {
          organizations: { groups: { uids: [groupId] } },
        },
      } satisfies SearchUsersRequest;
      return axios.get(`${customerMgmt}/users/search?${makeQueryByObject(requestOptions)}`, {});
    },
  },
};
